import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { validateInput } from '@repo/utils';
import RecipientTextField from '../../common/recipients/recipientList/RecipientTextField';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import type { IContact } from '../../lib/types';
import { getRecipientGroup } from '../../lib/utils';
import type { IRootState } from '../../store';
import * as envelopeHistoryActions from '../../store/envelopeHistory/actions';
import type { IRecipientBody, IEnvelopeHistoryBody } from '../../store/envelopeHistory/types';
import { updateEnvelopes } from '../../store/envelopeManagement/actions';
import type { IEnvelopeMetadata } from '../../store/envelopeManagement/types';

interface IState {
  envelope: IEnvelopeHistoryBody;
  envelopeManagementEnvelopes: IEnvelopeMetadata[];
  updateRecipientsError: boolean | null | undefined;
}

interface IProps {
  recipient: IRecipientBody;
  setRecipientToEdit: (recipient: IRecipientBody | null) => void;
  disabled?: boolean;
  contacts: IContact[];
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  envelope: state.envelopeHistory.envelope,
  envelopeManagementEnvelopes: state.envelopeManagement.envelopes,
  updateRecipientsError: state.envelopeHistory.updateRecipientsError,
  contacts: state.envelopeHistory.contacts,
});

const useStyles = makeStyles(() => ({
  wrapper: {
    marginTop: '20px',
  },
  nameWrapper: {
    display: 'flex',
  },
  actionButtons: {
    paddingTop: '56px',
    justifyContent: 'flex-end',
  },
  spacer: {
    width: '25px',
  },
}));

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const EditRecipientForm = (props: IProps) => {
  const classes = useStyles();
  const { recipient, disabled } = props;

  const { envelope, envelopeManagementEnvelopes, updateRecipientsError }: IState = useShallowSelector(selector);
  const [firstname, setFirstname] = useState(recipient.firstName || '');
  const [lastname, setLastname] = useState(recipient.lastName || '');
  const [email, setEmail] = useState(recipient.email || '');
  const dispatch = useDispatch();
  const { recipients } = envelope;

  const [error, setError] = useState({
    firstname: false,
    lastname: false,
    email: false,
  });

  useEffect(() => {
    if (updateRecipientsError === false) {
      props.setRecipientToEdit(null);
    }
    return () => {
      if (updateRecipientsError !== null) {
        dispatch(envelopeHistoryActions.updateRecipientsError(null));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [updateRecipientsError]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleEditRecipient = () => {
    let recipientToUpdate;
    const updatedRecipients = recipients.map((r) => {
      const group = getRecipientGroup(r, envelope);
      if (r.id === recipient.id) {
        recipientToUpdate = { ...recipient, firstName: firstname, lastName: lastname, email, group };
        return recipientToUpdate;
      }
      return { ...r, group };
    });

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    dispatch(envelopeHistoryActions.updateRecipient(recipientToUpdate, updatedRecipients, envelope.id));

    const updatedEnvelopes = envelopeManagementEnvelopes.map((e) => {
      if (e.id === envelope.id) {
        e.recipients = updatedRecipients;
      }
      return e;
    });

    dispatch(updateEnvelopes(updatedEnvelopes));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const areThereAnyErrors = () => {
    return Object.values(error).includes(true);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const validateInputValue = (e: any, str: string) => {
    return setError({ ...error, [str]: validateInput(e, str) });
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleContactSelect = (e: any, queryType: string) => {
    if (e) {
      setFirstname(e.firstName);
      setLastname(e.lastName);
      setEmail(e.email);
      validateInputValue(e.firstName, 'firstname');
      validateInputValue(e.lastName, 'lastname');
      validateInputValue(e.email, 'email');
    } else if (queryType === 'firstName') {
      setFirstname('');
    } else if (queryType === 'lastName') {
      setLastname('');
    } else {
      setEmail('');
    }
  };

  return (
    <div className={classes.wrapper}>
      <Typography variant="body2">Edit recipient details</Typography>
      <div className={classes.nameWrapper}>
        <div style={{ flex: 1 }}>
          <RecipientTextField
            autoFocus
            label="First Name"
            helperText={(error.firstname && 'Please enter a first name.') as string}
            placeholder="First Name"
            handleOnChange={(e) => {
              setFirstname(e.target.value);
              validateInputValue(e.target.value, 'firstname');
            }}
            error={error.firstname}
            value={firstname}
            dataSpec="edit-recipient-firstname"
            name="edit-recipient-firstname"
            disabled={disabled}
            contacts={props.contacts}
            handleContactSelect={handleContactSelect}
          />
        </div>
        <div className={classes.spacer} />
        <div style={{ flex: 1 }}>
          <RecipientTextField
            label="Last Name"
            helperText={(error.lastname && 'Please enter a last name.') as string}
            placeholder="Last name"
            handleOnChange={(e) => {
              setLastname(e.target.value);
              validateInputValue(e.target.value, 'lastname');
            }}
            error={error.lastname}
            value={lastname}
            dataSpec="edit-recipient-lastname"
            disabled={disabled}
            contacts={props.contacts}
            handleContactSelect={handleContactSelect}
          />
        </div>
      </div>
      <RecipientTextField
        label="Email"
        helperText={(error.email && 'Please enter an email.') as string}
        placeholder="Email"
        handleOnChange={(e) => {
          setEmail(e.target.value);
          validateInputValue(e.target.value, 'email');
        }}
        error={error.email}
        value={email}
        dataSpec="edit-recipient-email"
        disabled={disabled}
        contacts={props.contacts}
        handleContactSelect={handleContactSelect}
      />

      <DialogActions classes={{ root: classes.actionButtons }}>
        <Button
          data-spec="cancel"
          variant="text"
          style={{ marginRight: '24px' }}
          color="primary"
          onClick={() => props.setRecipientToEdit(null)}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          data-spec="saveButton"
          disabled={areThereAnyErrors()}
          onClick={handleEditRecipient}
          data-spect="edit-recipient-save-btn"
        >
          Save
        </Button>
      </DialogActions>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default EditRecipientForm;
